import { randomizationErrorKey } from "../Common/serverErrors";

/**
 * Which kind of refusal an allocation failure is. SFT-25.
 *
 * FOUR REFUSALS, FOUR REMEDIES, AND FOUR DIFFERENT PEOPLE WHO CARRY THEM OUT.
 * A single "could not randomize" banner makes them look like one fault:
 *
 * | Key | What is wrong | Who fixes it |
 * |---|---|---|
 * | `STRATUM_UNRESOLVED` | a factor question has no answer | the site, by completing the form |
 * | `STRATUM_NOT_GENERATED` | no sequence exists for this stratum | the designer, by generating the missing strata |
 * | `CONFIG_NOT_ACTIVE` / `NOT_ACTIVE` | the design is not in force | whoever holds `config_manage`, by activating it |
 * | `LIST_EXHAUSTED` | the stratum's numbers are used up | whoever may extend the list |
 *
 * Only the first is about the participant at all. Telling a coordinator to
 * chase an answer when the real problem is that nobody activated the design
 * sends them to the one place the fault is not.
 *
 * IN ITS OWN MODULE so the panel file exports only components — Vite's fast
 * refresh gives up on a file that mixes the two, and this is the module's
 * most-edited screen. Same reason `Common/idempotency.ts` exists.
 *
 * @category Randomization
 */

export type RefusalKind =
  | "notReady"
  | "noSequence"
  | "notInForce"
  | "exhausted";

export interface AllocationRefusal {
  /** Null when the failure is none of the four: render the server's message. */
  key: RefusalKind | null;
  severity: "error" | "warning";
}

/**
 * MATCHED ON THE ERROR KEY, NEVER ON THE MESSAGE TEXT.
 *
 * The server now translates these, so the body is a human sentence — and a
 * Spanish one on the Spanish locale. A `message.includes("LIST_EXHAUSTED")`
 * test worked only while the error mapping was broken and passed raw keys
 * through; it silently stopped matching when that was fixed on 10 September,
 * which is the sort of regression nothing fails on.
 */
export function allocationRefusal(error: unknown): AllocationRefusal {
  switch (randomizationErrorKey(error)) {
    case "STRATUM_UNRESOLVED":
      // A warning, not an error. A participant whose Week 4 form is not in yet
      // is not a fault condition — it is the normal state of every participant
      // on that study until Week 4.
      return { key: "notReady", severity: "warning" };
    case "STRATUM_NOT_GENERATED":
      return { key: "noSequence", severity: "error" };
    case "CONFIG_NOT_ACTIVE":
    case "NOT_ACTIVE":
      return { key: "notInForce", severity: "error" };
    case "LIST_EXHAUSTED":
      return { key: "exhausted", severity: "error" };
    default:
      return { key: null, severity: "error" };
  }
}
